import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Check, Star } from "lucide-react";
import api from "@/lib/api";
import { TierTable } from "@/components/PremiumPlans";

const TIERS = [
  { id: "guest", name: "Guest", price: "$0", period: "no account", features: ["5 decrees on one topic per visit", "Copy & share", "Bible reader in 3 translations"], cta: { to: "/generate", label: "Use as guest", testId: "pricing-guest-cta" } },
  { id: "free", name: "Free account", price: "$0", period: "forever", features: ["Full 10–15 decree sets, saved history", "Load more 3× a day, 5 lexicon lookups", "One 3-day plan, first lesson of each course", "Newsfeed with 5 posts a month"], cta: { to: "/auth", label: "Create free account", testId: "pricing-free-cta" } },
  { id: "premium", name: "Premium", price: "$4.99", period: "per month · or $49.99/yr", featured: true, features: ["Unlimited decrees, load-more and posts", "Plans up to 40 days, several at once", "All 9 translations, 4 highlight colors", "Every teaching, unlimited word studies", "Read-aloud everywhere · gift Premium"], cta: { to: "/premium", label: "Start 7-day free trial", testId: "pricing-premium-cta" } },
];

export function PricingSection() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="pricing-section">
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-4 lg:sticky lg:top-28">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A756E]">Pricing</p>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight mt-3">Free to begin. Premium when you're ready.</h2>
          <p className="font-body text-base text-[#4A4744] mt-4 leading-relaxed">Premium is $4.99 a month or $49.99 a year (save 17%), and every new subscriber gets 7 days free. Cancel any time from your profile.</p>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-3 gap-4">
          {TIERS.map((t) => (
            <div key={t.id} data-testid={`pricing-tier-${t.id}`}
              className={`flex flex-col border rounded-sm p-6 ${t.featured ? "border-[#9B7E4E] bg-[#FDFBF7] sm:-mt-4 sm:mb-4" : "border-[#D9D4CC] bg-[#FDFBF7]/60"}`}>
              <div className="flex items-center gap-2">
                {t.featured && <Crown className="w-4 h-4 text-[#9B7E4E]" />}
                <p className={`font-body text-xs uppercase tracking-[0.15em] ${t.featured ? "text-[#9B7E4E]" : "text-[#7A756E]"}`}>{t.name}</p>
              </div>
              <p className="font-heading text-4xl text-[#2C2A29] mt-4">{t.price}</p>
              <p className="font-body text-xs text-[#7A756E] mt-1">{t.period}</p>
              <ul className="mt-6 space-y-2.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2.5 font-body text-sm text-[#4A4744] leading-snug">
                    <Check className={`w-4 h-4 shrink-0 mt-0.5 ${t.featured ? "text-[#9B7E4E]" : "text-[#B7AE9F]"}`} /> {f}
                  </li>
                ))}
              </ul>
              <Link to={t.cta.to} data-testid={t.cta.testId}
                className={`mt-7 inline-flex justify-center px-4 py-3 text-sm rounded-sm transition-all duration-300 ${t.featured ? "bg-[#2C2A29] text-[#FAF9F6] hover:opacity-90" : "border border-[#D9D4CC] text-[#2C2A29] hover:bg-[#EAE6DF]"}`}>
                {t.cta.label}
              </Link>
            </div>
          ))}
        </div>
      </div>
      <details className="mt-10 border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm group" data-testid="pricing-compare">
        <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between font-body text-sm text-[#2C2A29] hover:bg-[#EAE6DF] transition-colors duration-300" data-testid="pricing-compare-toggle">
          Compare every feature across Guest, Free and Premium
          <span className="text-[#9B7E4E] text-xs uppercase tracking-wide group-open:hidden">Show</span>
          <span className="text-[#9B7E4E] text-xs uppercase tracking-wide hidden group-open:inline">Hide</span>
        </summary>
        <div className="px-6 pb-6"><TierTable testIdPrefix="landing" /></div>
      </details>
    </section>
  );
}

export function ReviewsSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reviews/public").then((r) => setData(r.data)).catch(() => setData({ count: 0, reviews: [] })); }, []);
  if (!data || data.reviews.length === 0) return null;
  return (
    <section className="bg-[#F3F0EA]/60 border-y border-[#EAE6DF]" data-testid="reviews-section">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-[#7A756E]">From members</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#2C2A29] tracking-tight mt-3">What believers are saying.</h2>
          </div>
          {data.average && (
            <p className="font-body text-sm text-[#4A4744] flex items-center gap-2" data-testid="reviews-average">
              <Star className="w-4 h-4" fill="#9B7E4E" color="#9B7E4E" /> {data.average} / 5 · {data.count} review{data.count === 1 ? "" : "s"}
            </p>
          )}
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-10">
          {data.reviews.map((r, i) => (
            <figure key={r.id} data-testid={`review-card-${i}`} className="border border-[#D9D4CC] bg-[#FDFBF7] rounded-sm p-6 flex flex-col">
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => <Star key={n} className="w-3.5 h-3.5" fill={n <= r.rating ? "#9B7E4E" : "none"} color={n <= r.rating ? "#9B7E4E" : "#D9D4CC"} />)}
              </div>
              <blockquote className="font-decree text-lg text-[#2C2A29] mt-4 leading-relaxed flex-1">“{r.comment}”</blockquote>
              <figcaption className="font-body text-xs uppercase tracking-wide text-[#7A756E] mt-4">{r.name}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
